import { ReactNode } from "react";
import { cn } from "~/lib/utils";
import { Timestamp } from "~/pages/manifold-nextgen/components/Timestamp";

export interface OverviewTile {
  label: string;
  content: ReactNode;
}

/** A row under the tiles, for a value too wide to sit in a column. */
export interface OverviewRow {
  label: string;
  content: ReactNode;
}

/**
 * `strip` is the full-width bordered panel used on an overview page; `stacked`
 * wraps into a grid for the narrow session-context sidebar, where a row of
 * columns would be unreadable.
 */
export type OverviewTileLayout = "strip" | "stacked";

const EYEBROW = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

export function OverviewTiles({
  tiles,
  rows = [],
  className,
  layout = "strip",
}: {
  tiles: OverviewTile[];
  rows?: OverviewRow[];
  className?: string;
  layout?: OverviewTileLayout;
}) {
  if (layout === "stacked") {
    return (
      <dl className={cn("grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3", className)}>
        {tiles.map((tile) => (
          <div key={tile.label} className="flex min-w-0 flex-col gap-1">
            <dt className={EYEBROW}>{tile.label}</dt>
            <dd className="text-sm text-foreground">{tile.content}</dd>
          </div>
        ))}
        {rows.map((row) => (
          <div key={row.label} className="col-span-full flex min-w-0 flex-col gap-1">
            <dt className={EYEBROW}>{row.label}</dt>
            <dd className="min-w-0 text-sm text-foreground">{row.content}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={cn("overflow-hidden rounded-xl border border-border bg-card", className)}>
      <div className="flex items-stretch">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex min-w-0 flex-1 flex-col justify-between gap-2 overflow-hidden border-l border-border px-4 py-3 first:border-l-0"
          >
            <dt className={EYEBROW}>{tile.label}</dt>
            <dd className="min-w-0 text-sm text-foreground">{tile.content}</dd>
          </div>
        ))}
      </div>

      {rows.map((row) => (
        // Label in a fixed gutter, pinned to the top: it has to stay beside the
        // first line once the value wraps to a second row of pills.
        <div key={row.label} className="flex items-start gap-4 border-t border-border px-4 py-3">
          <dt className={cn(EYEBROW, "w-24 shrink-0 pt-1")}>{row.label}</dt>
          <dd className="min-w-0 flex-1 text-sm text-foreground">{row.content}</dd>
        </div>
      ))}
    </dl>
  );
}

interface EntityOverviewHeaderProps {
  icon?: ReactNode;
  title?: string;
  /** What this is — "Dataset", "Personal dataset". Sits under the title. */
  type?: string;
  updatedAt?: string | null;
  /** Controls pinned to the top right of the header row, opposite the title. */
  actions?: ReactNode;
  tiles: OverviewTile[];
  rows?: OverviewRow[];
  tileLayout?: OverviewTileLayout;
  children: ReactNode;
  className?: string;
}

export function EntityOverviewHeader({
  icon,
  title,
  type,
  updatedAt,
  actions,
  tiles,
  rows,
  tileLayout,
  children,
  className,
}: EntityOverviewHeaderProps) {
  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {(icon || title || type || actions) && (
        <div className="flex items-start gap-3">
          {/* Centered on the title's first line, so it holds its place whether
              the byline is present and however far the title wraps. */}
          {icon && <div className="flex h-8 shrink-0 items-center">{icon}</div>}
          <div className="min-w-0 flex-1">
            {title && (
              <h1 className="min-w-0 break-words text-2xl font-bold text-foreground">{title}</h1>
            )}
            {type && (
              <p className="text-sm text-muted-foreground">
                {type}
                {updatedAt && (
                  <>
                    {" · updated "}
                    <Timestamp value={updatedAt} />
                  </>
                )}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}

      <OverviewTiles tiles={tiles} rows={rows} layout={tileLayout} />

      <div className="pt-2">{children}</div>
    </div>
  );
}
